import * as Sentry from '@sentry/browser'
import graphql from 'babel-plugin-relay/macro'
import { useCallback } from 'react'
import { usePortalMutation } from 'utils'

import {
  useAnalyticsHoseMutation,
  useAnalyticsHoseMutation$data,
} from './__generated__/useAnalyticsHoseMutation.graphql'
import { legacyGtmEventNameMapping, toLegacyGtmMeta } from './legacy'
import useAnalytics from './useAnalytics'

export type HubSpotAnalyticsEvent = Exclude<
  useAnalyticsHoseMutation['variables']['event'],
  // eslint-disable-next-line relay/no-future-added-value
  '%future added value'
>
export type HubSpotAnalyticsEventMetadata =
  useAnalyticsHoseMutation['variables']['meta']
type AsyncCommit = (
  event: HubSpotAnalyticsEvent,
  meta?: HubSpotAnalyticsEventMetadata,
) => Promise<useAnalyticsHoseMutation$data>

const mutation = graphql`
  mutation useAnalyticsHoseMutation(
    $event: HubSpotAnalyticsEvent!
    $meta: HubSpotAnalyticsEventMetadata
  ) {
    pushAnalyticsEvent(event: $event, metadata: $meta) {
      ok
    }
  }
`
/**
 * A hook that returns a function to log an event to multiple analytics services
 */
export function useAnalyticsHose() {
  const { logEvent: logGtmEvent } = useAnalytics()

  const [commit, isPushingEvent] = usePortalMutation<useAnalyticsHoseMutation>(
    mutation,
    {
      mutationName: 'PushAnalyticsEvent',
      showErrorToast: false, // Don't show toast for analytics failures
      sentryContext: {
        service: 'analytics',
      },
    },
  )

  /**
   * A wrapper around the commit function to promisify it
   */
  const commitAsync = useCallback<AsyncCommit>(
    (event: HubSpotAnalyticsEvent, meta?: HubSpotAnalyticsEventMetadata) =>
      new Promise((resolve, reject) =>
        commit({
          variables: {
            event,
            meta,
          },
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              resolve(response)
            }
          },
          onError: reject,
        }),
      ),
    [commit],
  )

  /**
   * Pushes an event and its metadata to multiple analytics services:
   * - Google Tag Manager
   * - HubSpot
   */
  const pushEvent = useCallback(
    async (
      event: HubSpotAnalyticsEvent,
      meta?: HubSpotAnalyticsEventMetadata,
    ) => {
      try {
        const gtmEventName = legacyGtmEventNameMapping[event] ?? event
        logGtmEvent(gtmEventName, toLegacyGtmMeta(meta))
        await commitAsync(event, meta)
      } catch (e) {
        Sentry.withScope((scope) => {
          scope.setTag(
            'useAnalyticsHose',
            'An error occurred while logging an event',
          )
          scope.setExtras({ event, meta })
          Sentry.captureException(e)
        })
      }
    },
    [commitAsync, logGtmEvent],
  )

  return {
    pushEvent,
    logGtmEvent,
    isPushingEvent,
  }
}
